export const PRC_TEMPLATE_CODES = new Set(['prc_tti', 'prc_milwaukee'])

export const isPrcAppraisal = appraisal => PRC_TEMPLATE_CODES.has(appraisal?.session_template)

const DEFAULT_PRC_FORM_CONFIG = {
  presentation_note: 'PRC PADR uses Performance Objectives and Cultural Expectation Assessment as the scored evaluation. Job Competence and IDP fields support development planning and are not included in the performance score.',
  objectives: {
    objective_label: '*目标 Goals / SMART- Specific / Measurable / Attainable / Relevant / Time-bound',
    success_indicators_label: '*衡量成功与否的关键指标 / Success Indicators',
    success_indicator_levels: [
      { key: 'min', label: 'Min / 最低' },
      { key: 'target', label: 'Target / 目标' },
      { key: 'stretch', label: 'Stretch / 挑战目标' }
    ],
    score_label: 'Rating (1 - 10)',
    score_max: 10
  },
  employee_information: {
    title: '员工个人信息及审批人 Employee Information & Approver',
    fields: [
      { key: 'staff_no', label: '工号 Staff No.' },
      { key: 'cn_name', label: '姓名 CN Name' },
      { key: 'en_name', label: '英文名 EN Name' },
      { key: 'position', label: '职位 Position' },
      { key: 'department', label: '部门 Department' },
      { key: 'padr_year', label: '*年度 PADR Year' },
      { key: 'first_level_approver', label: '*一级审批 *1st Level Approver' },
      { key: 'instruction', label: '填写说明 Instruction' },
      { key: 'cultural_expectation', label: '文化期望 Cultural Expectation' }
    ]
  },
  idp_fields: [
    { key: 'personal_strengths', label: 'Personal Strengths' },
    { key: 'develop_areas', label: 'Develop Areas' },
    { key: 'employee_year_end_evaluation', label: 'IDP Year End Evaluation - Employee' },
    { key: 'supervisor_year_end_suggestion', label: 'IDP Year End Suggestion - Supervisor' },
    { key: 'next_year_development_plan', label: 'Individual Development Plan for Next Year' },
    { key: 'next_year_training_plan', label: "Next Year's Training Plan" }
  ]
}

export const PRC_PRESENTATION_NOTE = DEFAULT_PRC_FORM_CONFIG.presentation_note

export const PRC_IDP_FIELDS = DEFAULT_PRC_FORM_CONFIG.idp_fields.map(field => [field.key, field.label])

export const PRC_RATING_GUIDE = [
  { code: 'UNS', range: '0 <= 整体评分 < 4 / 0 <= Score < 4' },
  { code: 'NI', range: '4 <= 整体评分 < 6 / 4 <= Score < 6' },
  { code: 'ME', range: '6 <= 整体评分 < 8 / 6 <= Score < 8' },
  { code: 'EE', range: '8 <= 整体评分 < 9 / 8 <= Score < 9' },
  { code: 'OS', range: '9 <= 整体评分 <= 10 / 9 <= Score <= 10' }
]

export const getPrcFormConfig = formConfig => ({
  ...DEFAULT_PRC_FORM_CONFIG,
  ...(formConfig || {}),
  objectives: {
    ...DEFAULT_PRC_FORM_CONFIG.objectives,
    ...((formConfig || {}).objectives || {})
  },
  employee_information: {
    ...DEFAULT_PRC_FORM_CONFIG.employee_information,
    ...((formConfig || {}).employee_information || {}),
    fields: Array.isArray(((formConfig || {}).employee_information || {}).fields)
      ? formConfig.employee_information.fields
      : DEFAULT_PRC_FORM_CONFIG.employee_information.fields
  },
  idp_fields: Array.isArray((formConfig || {}).idp_fields) ? formConfig.idp_fields : DEFAULT_PRC_FORM_CONFIG.idp_fields
})

export const prcObjectiveScoreMax = (appraisal, formConfig) => {
  if (!isPrcAppraisal(appraisal)) return 4
  const maxScore = Number(getPrcFormConfig(formConfig).objectives.score_max)
  return Number.isFinite(maxScore) && maxScore > 0 ? maxScore : 10
}

export const prcObjectiveScoreLabel = (appraisal, formConfig) => {
  if (!isPrcAppraisal(appraisal)) {
    return formConfig?.objectives?.score_label || `Score (1-${prcObjectiveScoreMax(appraisal, formConfig)})`
  }
  return getPrcFormConfig(formConfig).objectives.score_label || `Score (1-${prcObjectiveScoreMax(appraisal, formConfig)})`
}

export const prcPresentationNote = formConfig => getPrcFormConfig(formConfig).presentation_note

export const prcObjectiveConfig = formConfig => getPrcFormConfig(formConfig).objectives

export const prcIdpFields = formConfig => getPrcFormConfig(formConfig).idp_fields.map(field => [field.key, field.label])

const fullName = (firstName, lastName) => `${firstName || ''} ${lastName || ''}`.trim()

export const prcEmployeeInformationTitle = formConfig => getPrcFormConfig(formConfig).employee_information.title

export const prcEmployeeInformationFields = (appraisal, formConfig) => {
  const employeeName = fullName(appraisal?.employee_first_name, appraisal?.employee_last_name)
  const managerName = fullName(appraisal?.manager_first_name, appraisal?.manager_last_name)
  const padrYear = String(appraisal?.session_end_date || appraisal?.session_start_date || '').slice(0, 4)
  const values = {
    staff_no: appraisal?.employee_company_id || appraisal?.employee || '—',
    cn_name: '—',
    en_name: employeeName || '—',
    position: appraisal?.employee_position || '—',
    department: appraisal?.department_name || '—',
    padr_year: padrYear || '—',
    first_level_approver: managerName || appraisal?.manager_email || '—',
    instruction: 'Click Here',
    cultural_expectation: 'Click Here'
  }
  return getPrcFormConfig(formConfig).employee_information.fields.map(field => ({
    ...field,
    value: values[field.key] || '—'
  }))
}

export const prcSuccessIndicatorField = key => `success_indicator_${key}`

export const getPrcSuccessIndicatorValue = (objective, key) => {
  const directValue = objective?.[prcSuccessIndicatorField(key)]
  if (directValue !== undefined && directValue !== null && directValue !== '') return directValue
  return key === 'target' ? (objective?.success_indicators || '') : ''
}

export const prcEvaluationLevel = (score) => {
  const value = parseFloat(score)
  if (!Number.isFinite(value)) return null
  if (value < 4) return 'UNS'
  if (value < 6) return 'NI'
  if (value < 8) return 'ME'
  if (value < 9) return 'EE'
  return 'OS'
}
